// details 전체에서 최상위 numbered line만 추출해 overview용 문자열로 반환
export function extractOverviewFromDetails(details) {
  return parseDetailLines(details)
    .map(line => String(line || '').trim())
    .filter(line => /^\d+\.\s+.+$/.test(line))
    .join('\n');
}
export const TOP_LEVEL_NUMBERED_LINE_REGEX = /^\d+\.\s+.+$/;
const NUMBERING_LINE_REGEX = /^(\d+(?:\.\d+)*)(?:\.)?(?:\s+.*)?$/;
const IMAGE_MARKER_REGEX = /^\[image:(\d+)(?:\|([^\]]*))?\]$/i;

// 상세 본문 파싱/렌더링에서 공통 사용하는 규칙 모음입니다.
// notice/editor 페이지가 동일한 텍스트 규칙을 공유하도록 캡슐화합니다.

// 상세 입력 텍스트를 줄 단위로 분리하는 기본 유틸입니다.
export function parseDetailLines(text) {
  return String(text || '').split(/\r?\n/);
}

// 마크다운 해시(#) 제목을 화면 표기용 기호(◎)로 치환합니다.
export function replaceHashHeadingMarker(text) {
  return String(text || '').replace(/^#{1,6}\s*/gm, '◎ ');
}

// "1.", "1.2", "1.2.3" 같은 번호 패턴의 깊이를 계산합니다.
export function getNumberingLevel(text) {
  const match = String(text || '').trim().match(NUMBERING_LINE_REGEX);
  if (!match) {
    return null;
  }

  return match[1].split('.').length;
}

// 번호 깊이를 제목 체계 레벨로 변환합니다. (기준: #=1, 1.=2, 1.1.=3)
export function getHierarchicalNumberingLevel(text) {
  const numberingLevel = getNumberingLevel(text);
  if (numberingLevel === null) {
    return null;
  }

  return numberingLevel + 1;
}

// 계층 레벨에 따라 들여쓰기/폰트 크기/굵기를 계산합니다.
export function getLevelTextStyle(level, forceNormalWeight = false) {
  const normalizedLevel = Math.max(1, Number(level) || 1);
  const indent = Math.max(0, (normalizedLevel - 1) * 20);

  if (normalizedLevel === 1) {
    return {
      paddingLeft: `${indent}px`,
      fontSize: '20px',
      fontWeight: forceNormalWeight ? 400 : 700,
      color: 'var(--hcps-level1-text)',
    };
  }

  if (normalizedLevel === 2) {
    return {
      paddingLeft: `${indent}px`,
      fontSize: '18px',
      fontWeight: forceNormalWeight ? 400 : 700,
      color: 'var(--hcps-level2-text)',
    };
  }

  if (normalizedLevel === 3) {
    return {
      paddingLeft: `${indent}px`,
      fontSize: '16px',
      fontWeight: forceNormalWeight ? 400 : 500,
      color: 'var(--hcps-level3-text)',
    };
  }

  return {
    paddingLeft: `${indent}px`,
    fontSize: '14px',
    fontWeight: forceNormalWeight ? 400 : 500,
    color: 'var(--hcps-level4-text)',
  };
}

// 현재 제목 레벨 기준으로 본문이 따라갈 하위 레벨을 계산합니다.
export function getChildContentLevel(headingLevel) {
  return Math.min(headingLevel + 1, 4);
}

// [image:n] 마커에서 이미지 인덱스를 추출합니다.
export function getImageMarkerIndex(text) {
  const match = String(text || '').trim().match(IMAGE_MARKER_REGEX);
  if (!match) {
    return null;
  }

  const markerIndex = Number(match[1]) - 1;
  return markerIndex >= 0 ? markerIndex : null;
}

// [image:n|caption] 마커에서 주석 텍스트를 추출합니다.
export function getImageMarkerCaption(text) {
  const match = String(text || '').trim().match(IMAGE_MARKER_REGEX);
  if (!match) {
    return '';
  }

  return String(match[2] || '').trim();
}

// 상세 본문을 렌더링용 블록 구조(빈 줄/소제목/일반 텍스트)로 변환합니다.
export function parseDetailContentLines(text) {
  return parseDetailLines(text).map((line) => {
    if (!line.trim()) {
      return {
        type: 'blank',
        text: '',
        level: 1,
      };
    }

    const hashHeadingMatch = line.match(/^(\s*)(#{1,6})(?:\s*(.*))?$/);
    if (hashHeadingMatch) {
      const headingText = (hashHeadingMatch[3] || '').trim();
      return {
        type: 'subheading',
        text: headingText
          ? `${hashHeadingMatch[1]}${hashHeadingMatch[2]} ${headingText}`
          : `${hashHeadingMatch[1]}${hashHeadingMatch[2]}`,
        level: hashHeadingMatch[2].length,
      };
    }

    return {
      type: 'text',
      text: line,
      level: getNumberingLevel(line) || 1,
    };
  });
}

// "1. 제목" 또는 "# 제목" 패턴을 섹션 단위로 파싱합니다.
export function parseUpdateNoticeDetailItems(text) {
  const lines = parseDetailLines(text);
  const items = [];
  let currentItem = null;
  let nextOrder = 1;

  const flushCurrentItem = () => {
    if (!currentItem) {
      return;
    }
    items.push({
      ...currentItem,
      content: currentItem.content.trim(),
    });
    currentItem = null;
  };

  lines.forEach((line) => {
    const trimmed = line.trim();
    const numberedMatch = trimmed.match(/^(\d+)\.\s+(.+)$/);
    const hashHeadingMatch = trimmed.match(/^(#{1,6})(?:\s*(.*))?$/);

    if (numberedMatch && currentItem?.isHashHeading) {
      currentItem.content = currentItem.content
        ? `${currentItem.content}\n${line}`
        : line;
      return;
    }

    if (numberedMatch) {
      flushCurrentItem();
      const explicitOrder = Number(numberedMatch[1]);
      const order = Number.isFinite(explicitOrder) ? explicitOrder : nextOrder;
      currentItem = {
        id: `detail-${items.length + 1}`,
        title: numberedMatch[2].trim(),
        order,
        isHashHeading: false,
        content: '',
      };
      nextOrder = order + 1;
      return;
    }

    if (hashHeadingMatch) {
      flushCurrentItem();
      const headingTitle = (hashHeadingMatch[2] || '').trim();
      currentItem = {
        id: `detail-${items.length + 1}`,
        title: headingTitle,
        order: nextOrder,
        isHashHeading: true,
        content: '',
      };
      return;
    }

    if (!currentItem) {
      return;
    }
    currentItem.content = currentItem.content
      ? `${currentItem.content}\n${line}`
      : line;
  });

  flushCurrentItem();
  return items;
}

// [image:n] 마커를 실제 이미지 소스와 매핑합니다.
export function resolveMarkerImage(line, images) {
  const token = String(line || '').trim().match(IMAGE_MARKER_REGEX);
  if (!token) return null;
  const index = Number(token[1]) - 1;
  if (index < 0) return null;
  return images[index] || null;
}

// 상세 텍스트에서 최상위 번호 라인만 추출합니다.
export function extractTopLevelNumberedLines(text) {
  return parseDetailLines(text)
    .map((line) => String(line || '').trim())
    .filter((line) => TOP_LEVEL_NUMBERED_LINE_REGEX.test(line));
}

// 프로그램/플랫폼/버전을 조합해 자동 제목을 생성합니다.
export function buildAutoTitle(program, platform, version) {
  const normalizedProgram = String(program || '').trim();
  const normalizedPlatform = String(platform || '').trim();
  const normalizedVersion = String(version || '').trim();

  const parts = [
    normalizedProgram,
    normalizedPlatform || null,
    normalizedVersion,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(' ') : 'Update Notice';
}
